import { skipToken } from "@reduxjs/toolkit/query";
import {
  collectPlaybookCriteria,
  getOutcomeDefinitionFromPlaybook,
  playbookIdSchema,
} from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  buildTeammateDisplayNames,
} from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import type { ChatSuggestion } from "@repo/ui/chat/chatEmptyState";
import { useMatch } from "react-router";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { appPaths } from "../../app/appPaths.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { usePlaybookIdParam } from "../../hooks/usePlaybookIdParam.js";
import { getPlaybookConfigSuggestions } from "./copilotPlaybookConfigSuggestions.js";

/** Shown when no route produces more specific suggestions. */
const fallbackSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize playbook performance",
    prompt:
      "Summarize the team's playbook performance over the past two weeks. Highlight the strongest and weakest criteria with examples.",
  },
  {
    label: "Find coaching risks",
    prompt:
      "Which teammates show coaching risks based on their recent calls? Include specific patterns and transcript examples.",
  },
  {
    label: "Research a topic",
    prompt:
      "What are the most common themes customers bring up in recent calls? Summarize the top topics with examples.",
  },
];

/**
 * Contextual prompt suggestions for the copilot's empty state, keyed off the
 * current route (mirroring the pre-app-shell suggestion pills).
 */
export function useCopilotSuggestions(): ChatSuggestion[] {
  // Match route families to determine context
  const teammateRouteMatch = useMatch(`${appPaths.teammate.pattern}/*`);
  const teammateAgentRouteMatch = useMatch(
    `${appPaths.teammateAgent.pattern}/*`,
  );

  // Match specific team page views
  const byCustomerViewMatch = useMatch(`${appPaths.playbookCalls.pattern}/*`);
  const byPlaybookViewMatch = useMatch(appPaths.playbookCriteria.pattern);
  const byCriterionViewMatch = useMatch(appPaths.playbookCriterion.pattern);

  // The playbook configuration surface: the editor and its tabs, matched
  // separately from the team playbook views above.
  const configEditorMatch = useMatch(`${appPaths.settingsPlaybook.pattern}/*`);

  // Extract IDs from route matches. Teammate URLs nest under the playbook, so
  // the playbook hook covers both route families.
  const playbookId = usePlaybookIdParam();
  const teammateId = teammateRouteMatch?.params.teammateId
    ? asTeammateId(teammateRouteMatch.params.teammateId)
    : undefined;
  const teammateAgentId = teammateAgentRouteMatch?.params.teammateAgentId;
  const criterionId = byCriterionViewMatch?.params.criterionId;

  // Fetch data for contextual suggestions
  const { data: teammate } = useBasicTeammateQuery(teammateId ?? skipToken);
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const { data: teammateAgent } = useGetTeammateAgentQuery(
    teammateAgentId ?? skipToken,
  );

  const outcomeDefinition = playbook
    ? getOutcomeDefinitionFromPlaybook(playbook)
    : undefined;

  // The playbook configuration surface (editor + tabs) gets playbook-shaping
  // starters — draft an outcome, draft or extend the playbook, surface KB gaps —
  // in place of the generic research prompts. A not-yet-loaded playbook reads as
  // empty, which is the "draft it" case, so a brand-new playbook still gets them.
  const configPlaybookId = playbookIdSchema.safeParse(
    configEditorMatch?.params.playbookId,
  ).data;
  const { data: configPlaybook } = usePlaybookByIdQuery(
    configPlaybookId ?? skipToken,
  );
  const isKnowledgeBaseEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.KnowledgeBase,
  );
  if (configEditorMatch) {
    return getPlaybookConfigSuggestions({
      playbookTree: configPlaybook?.assessment_tree,
      outcomeDefinition: configPlaybook
        ? getOutcomeDefinitionFromPlaybook(configPlaybook)
        : undefined,
      isKnowledgeBaseEnabled: isKnowledgeBaseEnabled ?? false,
    });
  }

  if (teammateId && teammate) {
    const teammateDisplayNames = buildTeammateDisplayNames(teammate);

    // Show secondary display name, since email may help distinguish teammates
    const teammateName = teammateDisplayNames.secondaryDisplayName
      ? `${teammateDisplayNames.primaryDisplayName} (${teammateDisplayNames.secondaryDisplayName})`
      : teammateDisplayNames.primaryDisplayName;

    // User is reviewing an individual rep — likely preparing for coaching or
    // investigating performance
    const suggestions: ChatSuggestion[] = [
      {
        label: "Prepare coaching notes",
        prompt: `Prepare actionable coaching notes for ${teammateName} based on their calls from the past 2 weeks. Include specific strengths to reinforce and areas for improvement with examples and transcript quotes.`,
      },
      {
        label: "Pull transcript highlights",
        prompt: `Find transcript highlights and lowlights for ${teammateName} from the past few weeks. Include specific quotes that demonstrate both strong performance and coaching opportunities.`,
      },
    ];

    if (outcomeDefinition) {
      const outcomeName = outcomeDefinition.name.toLowerCase();
      suggestions.push({
        label: `Analyze their ${outcomeName} rate`,
        prompt: `Analyze why the ${outcomeName} rate for ${teammateName} is where it is. What specific behaviors or patterns in their calls are contributing to this? What could they do differently?`,
      });
    }

    return suggestions;
  }

  if (playbookId && !teammateId) {
    if (byCustomerViewMatch) {
      // Customer interactions view — users are looking at customer calls
      return [
        {
          label: "Find common objections",
          prompt:
            "What are the most common objections customers are raising in recent calls? Include specific examples and how reps are handling them.",
        },
        {
          label: "Review negative calls",
          prompt:
            "Which recent calls had negative customer sentiment? Summarize what went wrong and identify patterns.",
        },
        {
          label: "Compile positive quotes",
          prompt:
            "Compile positive customer quotes from recent calls. Include specific quotes that demonstrate what the team is doing well.",
        },
      ];
    }

    if (byCriterionViewMatch && criterionId) {
      // Criterion drill-down — users are looking at a specific criterion
      const criterionName = playbook
        ? collectPlaybookCriteria(playbook.assessment_tree).find(
            (criterion) => criterion.id === criterionId,
          )?.name
        : undefined;

      if (criterionName) {
        return [
          {
            label: "Prepare coaching notes for this criteria",
            prompt: `Prepare actionable coaching notes for the team on the "${criterionName}" criteria. Include specific examples from recent calls and recommendations for improvement.`,
          },
        ];
      }
    }

    if (byPlaybookViewMatch) {
      // Criteria adherence view
      const suggestions: ChatSuggestion[] = [
        {
          label: "What are the biggest improvement opportunities?",
          prompt:
            "Which playbook criteria is the team struggling with most? Provide specific examples and recommendations for improvement.",
        },
      ];

      if (outcomeDefinition) {
        const outcomeName = outcomeDefinition.name.toLowerCase();
        suggestions.push({
          label: `What's impacting ${outcomeName} most?`,
          prompt: `Which playbook criteria have the biggest impact on ${outcomeName}? Analyze the correlation between criteria adherence and outcomes.`,
        });
      }

      return suggestions;
    }

    return fallbackSuggestions;
  }

  if (teammateAgentId) {
    // User is configuring/reviewing an AI agent — likely checking performance
    const agentName = teammateAgent?.name ?? "this agent";
    return [
      {
        label: "Identify gaps in performance",
        prompt: `What objections or scenarios is ${agentName} struggling with? Provide specific examples from recent calls and suggestions for improvement.`,
      },
    ];
  }

  return fallbackSuggestions;
}
